import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Button } from '../Button/Button';

import './Modal.scss';

export const Modal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  children,
  confirmText = 'Confirm',
  confirmVariant = 'primary',
  closeText = 'Cancel',
  showConfirmButton = true,
  showCloseButton = true,
  showFooter = true,
  width = '',
  height = '',
  maxHeight = 'auto',
  modalBodyClassName = '',
  modalOverlayClassName = '',
  modalContentClassName = '',
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow || '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target) && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className={`modal-overlay ${modalOverlayClassName}`}>
      <div
        className={`modal-content ${modalContentClassName}`}
        ref={modalRef}
        style={{ width, height, maxHeight }}
      >
        {title && <div className='modal-header'>{title}</div>}

        <div className={`modal-body ${modalBodyClassName}`}>{children}</div>

        {showFooter && (
          <div className='modal-footer'>
            {showCloseButton && (
              <Button variant='ghost' onClick={onClose}>
                {closeText}
              </Button>
            )}

            {showConfirmButton && (
              <Button variant={confirmVariant} onClick={onConfirm}>
                {confirmText}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  title: PropTypes.string,
  children: PropTypes.node,
  confirmText: PropTypes.string,
  closeText: PropTypes.string,
  showConfirmButton: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  showFooter: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.string,
};
